import React from 'react';
import { useRouter } from 'next/router';

import DataModel from '@/shared/utils/redux/data';
import Helper from '@/shared/utils/redux/helper';
import ModalBottomSheet from '@/shared/components/modal-bottom-sheet';
import { ErrorPopup } from '@/shared/utils/redux/types';
import { useRedirect } from '@/shared/utils/router';
import { Box } from '@mui/material';
import {
  ErrorPopupStyle,
  MultipleButtonErrorPopupStyle,
  SingleButtonErrorPopupStyle,
} from './style';
import Button from '../button';
import ImageGenerator from '../image';

const ErrorPopupComponent = () => {
  const pathnameRef = React.useRef<string | null>(null);
  // CUSTOM HOOKS
  const { errorPopup } = DataModel();
  const { setErrorPopup } = Helper();
  const { redirect } = useRedirect();
  const { pathname } = useRouter();
  const {
    open = false,
    title = null,
    subtitle = null,
    action: actions,
    type,
    closeButton = true,
    closeRedirectUrl = null,
    errorCode,
    cornerClose = false,
  } = errorPopup as ErrorPopup;
  React.useEffect(() => {
    if (pathnameRef.current && pathnameRef.current != pathname) {
      setErrorPopup({
        open: false,
      });
    }
    pathnameRef.current = pathname;
  }, [pathname]);
  // CALLBACK
  const onOpenPopup = React.useCallback(
    (state: boolean) => {
      setErrorPopup({
        open: state,
      });
    },
    [setErrorPopup],
  );

  const onRedirect = React.useCallback(
    (url: string) => {
      redirect({
        url,
      });
    },
    [redirect],
  );

  const onClose = React.useCallback(() => {
    if (closeRedirectUrl) {
      redirect({
        url: closeRedirectUrl,
      });
    } else {
      onOpenPopup(false);
    }
  }, [closeRedirectUrl]);
  // MEMOIZED VALUE
  const singleButton = React.useMemo(() => {
    if (actions && Array.isArray(actions) && actions.length === 1) {
      return true;
    }
    return false;
  }, [actions]);
  const multipleButton = React.useMemo(() => {
    if (actions && Array.isArray(actions) && actions.length === 2) {
      return true;
    }
    return false;
  }, [actions]);
  const Action = React.useMemo(() => {
    if (actions && Array.isArray(actions) && actions.length > 0) {
      return (
        <div
          className={
            actions.length === 1 ? 'error-popup_action-single' : 'error-popup_action-multiple'
          }
        >
          {actions.map(
            ({ id, colorScheme = 'primary', word = 'Tutup', type = 'close', redirectUrl = '' }) => {
              const onClick =
                type === 'close'
                  ? { onClick: () => onOpenPopup(false) }
                  : { onClick: () => onRedirect(redirectUrl) };
              return (
                <Button
                  key={id}
                  id={id}
                  colorScheme={colorScheme}
                  sx={{ boxShadow: 'none' }}
                  variant="outlined"
                  size="large"
                  {...onClick}
                >
                  {word}
                </Button>
              );
            },
          )}
        </div>
      );
    }
    return null;
  }, [actions, onOpenPopup, onRedirect]);
  const StyleWrapper = React.useCallback(
    ({ children }: any) => {
      if (multipleButton) {
        return (
          <MultipleButtonErrorPopupStyle className={type}>{children}</MultipleButtonErrorPopupStyle>
        );
      } else if (singleButton) {
        return (
          <SingleButtonErrorPopupStyle className={type}>{children}</SingleButtonErrorPopupStyle>
        );
      }
      return <ErrorPopupStyle className={type}>{children}</ErrorPopupStyle>;
    },
    [singleButton, multipleButton, type],
  );
  const Image = React.useMemo(() => {
    if (singleButton) {
      if (
        errorCode?.toUpperCase() === 'TEMP_BLOCKED' ||
        errorCode?.toUpperCase() === 'BLOCKED' ||
        errorCode?.toUpperCase() === 'REGISTRATION_BLOCKED' ||
        errorCode?.toUpperCase() === 'UNAUTHORIZED' ||
        errorCode?.toUpperCase() === 'FREEZED_MOBILE_NUMBER'
      ) {
        return (
          <div className="error-popup_image">
            <ImageGenerator
              path="/web%2Fimg%2Fbunny_warning.png"
              alt="Error"
              width={208}
              height={164}
            />
          </div>
        );
      }
      if (errorCode?.toUpperCase() === 'GENERAL_WEB_CLOSE_ACCOUNT') {
        return (
          <div className="error-popup_image">
            <ImageGenerator
              path="/web%2Fimg%2Fbunny_sweeping.png"
              alt="Error"
              width={183}
              height={163}
            />
          </div>
        );
      }
      return (
        <div className="error-popup_image">
          <ImageGenerator path="/web%2Fimg%2Fbunny_stop.png" alt="Error" width={165} height={140} />
        </div>
      );
    } else if (multipleButton) {
      return null;
    }
    return (
      <div className="error-popup_image">
        <ImageGenerator
          path="/web%2Fimg%2Fbunny_sweeping.png"
          alt="Error"
          width={183}
          height={163}
        />
      </div>
    );
  }, [errorCode, singleButton, multipleButton]);
  return (
    <>
      <ModalBottomSheet
        open={open}
        onOpen={() => onOpenPopup(true)}
        onClose={() => onOpenPopup(false)}
        withHandle={false}
        type={type}
      >
        <StyleWrapper>
          {cornerClose && (
            <Box onClick={onClose}>
              {closeButton && (
                <ImageGenerator
                  path="/web%2Fic%2Fclose_dark.png"
                  alt="Close"
                  width={12}
                  height={12}
                />
              )}
            </Box>
          )}
          <div className="error-popup_close-ico" onClick={onClose}>
            {closeButton && !cornerClose && (
              <ImageGenerator
                path="/web%2Fic%2Fclose_dark.png"
                alt="Close"
                width={12}
                height={12}
              />
            )}
          </div>
          <div className="error-popup_content">
            <div className="error-popup_message">
              {multipleButton && (
                <div className="error-popup_exclamation-ico">
                  <ImageGenerator
                    path="/web%2Fic%2Fexclamation_red.png"
                    alt="Error"
                    width={44}
                    height={44}
                  />
                </div>
              )}
              {Image}
              <div>
                {title && (
                  <div id="title_sheet_message" className="error-popup_title">
                    {title}
                  </div>
                )}
                {subtitle && (
                  <div id="info_sheet_message" className="error-popup_subtitle">
                    {subtitle}
                  </div>
                )}
              </div>
            </div>
            <div className="error-popup_action">{Action}</div>
          </div>
        </StyleWrapper>
      </ModalBottomSheet>
    </>
  );
};

export default ErrorPopupComponent;
